/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PbxCliButtonConfigsFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing PBX CLI button configurations.
    Manages configurable button actions for PBX CLI interface.

 🔧 Notes:
    - Only fs_cli -x "command" format allowed
    - Maximum of 24 configurations per tenant

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Terminal } from 'lucide-react';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { FormBox } from '../../components/ui/FormBox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import Toggle from '../../components/ui/Toggle';
import { Textarea } from '../../components/ui/textarea';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { ModuleFormHeader } from '../../components/layouts';
import {
  fetchPbxCliButtonConfig,
  createPbxCliButtonConfig,
  updatePbxCliButtonConfig,
  deletePbxCliButtonConfig,
} from './api';
import { PbxCliButtonConfigFormState } from './types';

type ButtonConfigForm = PbxCliButtonConfigFormState;

const DEFAULT_FORM: ButtonConfigForm = {
  label: '',
  tooltip: '',
  command: '',
  orderIndex: 0,
  enabled: true,
};

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeResponse = (payload: any): ButtonConfigForm => ({
  label: payload.label || '',
  tooltip: payload.tooltip || '',
  command: payload.command || '',
  orderIndex: payload.orderIndex ?? payload.order_index ?? 0,
  enabled: Boolean(payload.enabled ?? true),
});

const shouldValidateFsCli = (command: string) => command.trim().toLowerCase().startsWith('fs_cli');

const isValidFsCliCommand = (command: string) => /^fs_cli\s+-x\s+"[^"]+"$/i.test(command.trim());

export default function PbxCliButtonConfigsFormView() {
  const { t } = useI18n();
  const { showError, showInfo, showSuccess } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<ButtonConfigForm>(deepClone(DEFAULT_FORM));
  const [originalData, setOriginalData] = useState<ButtonConfigForm>(deepClone(DEFAULT_FORM));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const lastSaveTimestamp = useRef(0);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('pbxCli.buttonConfigs.form.editTitle')} - ${formData.label || ''}` : t('pbxCli.buttonConfigs.form.createTitle')),
    [isEditMode, t, formData.label],
  );

  const applyLoadedData = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (payload: any, { duplicate }: { duplicate: boolean }) => {
      const normalized = normalizeResponse(payload);
      if (duplicate) {
        const copySuffix = t('common:copySuffix');
        normalized.label = `${normalized.label} ${copySuffix}`.trim();
        setOriginalData(deepClone(DEFAULT_FORM));
      } else {
        setOriginalData(deepClone(normalized));
      }
      setFormData(deepClone(normalized));
    },
    [t],
  );

  const loadButtonConfig = useCallback(
    async (buttonId: string, { duplicate }: { duplicate: boolean }) => {
      setIsLoading(true);
      try {
        const result = await fetchPbxCliButtonConfig(buttonId);
        applyLoadedData(result, { duplicate });
      } catch (error) {
        showError(t('pbxCli.buttonConfigs.messages.loadError'));
        navigate('/pbx/tools/cli-button-configs');
      } finally {
        setIsLoading(false);
      }
    },
    [applyLoadedData, navigate, showError, t],
  );

  useEffect(() => {
    if (isEditMode && id) {
      loadButtonConfig(id, { duplicate: false });
    } else if (!isEditMode && duplicateId) {
      loadButtonConfig(duplicateId, { duplicate: true });
    }
  }, [id, duplicateId, isEditMode, loadButtonConfig]);

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData],
  );

  const handleFieldChange = <K extends keyof ButtonConfigForm>(field: K, value: ButtonConfigForm[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.label.trim()) {
      nextErrors.label = t('pbxCli.buttonConfigs.validation.labelRequired');
    }

    if (!formData.command.trim()) {
      nextErrors.command = t('pbxCli.buttonConfigs.validation.commandRequired');
    } else if (shouldValidateFsCli(formData.command) && !isValidFsCliCommand(formData.command)) {
      nextErrors.command = t('pbxCli.buttonConfigs.validation.invalidFsCli');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();

      if (!validateForm()) {
        showError(t('pbxCli.buttonConfigs.messages.validationFailed'));
        return;
      }

      // hasChanges validation removed

      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return;
      }
      lastSaveTimestamp.current = now;

      setIsSaving(true);
      try {
        const payload = {
          label: formData.label.trim(),
          tooltip: formData.tooltip.trim() || null,
          command: formData.command.trim(),
          orderIndex: Number.isFinite(formData.orderIndex) ? formData.orderIndex : 0,
          enabled: formData.enabled,
        };

        if (isEditMode) {
          await updatePbxCliButtonConfig(id!, payload);
        } else {
          await createPbxCliButtonConfig(payload);
        }

        showSuccess(
          isEditMode
            ? t('pbxCli.buttonConfigs.messages.updateSuccess')
            : t('pbxCli.buttonConfigs.messages.createSuccess'),
        );

        if (isEditMode) {
          setOriginalData(deepClone(formData));
        }
        navigate('/pbx/tools/cli-button-configs');
      } catch (error) {
        showError(error.message || t('pbxCli.buttonConfigs.messages.saveError'));
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, id, isEditMode, navigate, showError, showInfo, showSuccess, t, validateForm],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deletePbxCliButtonConfig(id);

      showSuccess(t('pbxCli.buttonConfigs.messages.deleteSuccess'));
      navigate('/pbx/tools/cli-button-configs');
    } catch (error) {
      showError(error.message || t('pbxCli.buttonConfigs.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const handleCancel = () => {
    if (isEditMode) {
      setFormData(deepClone(originalData));
      setErrors({});
    } else {
      navigate('/pbx/tools/cli-button-configs');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Terminal className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('pbxCli.buttonConfigs.actions.backToList')}
        onBack={() => navigate('/pbx/tools/cli-button-configs')}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <FormBox title={t('pbxCli.buttonConfigs.form.generalTitle')} icon={<Terminal className="h-5 w-5" />}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="label">
                {t('pbxCli.buttonConfigs.fields.label')}
                <span className="ml-1 text-red-500">*</span>
              </Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(event) => handleFieldChange('label', event.target.value)}
                placeholder={t('pbxCli.buttonConfigs.placeholders.label')}
                className={errors.label ? 'border-red-500' : ''}
              />
              {errors.label && <p className="mt-1 text-sm text-red-500">{errors.label}</p>}
            </div>

            <div>
              <Label htmlFor="tooltip">{t('pbxCli.buttonConfigs.fields.tooltip')}</Label>
              <Textarea
                id="tooltip"
                value={formData.tooltip}
                onChange={(event) => handleFieldChange('tooltip', event.target.value)}
                placeholder={t('pbxCli.buttonConfigs.placeholders.tooltip')}
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="command">
                {t('pbxCli.buttonConfigs.fields.command')}
                <span className="ml-1 text-red-500">*</span>
                <SimpleTooltip content={t('pbxCli.buttonConfigs.help.commandFormat')} position="top">
                  <span className="ml-1 text-xs text-muted-foreground">(?)</span>
                </SimpleTooltip>
              </Label>
              <Input
                id="command"
                value={formData.command}
                onChange={(event) => handleFieldChange('command', event.target.value)}
                placeholder='fs_cli -x "status"'
                className={`font-mono ${errors.command ? 'border-red-500' : ''}`}
              />
              {errors.command && <p className="mt-1 text-sm text-red-500">{errors.command}</p>}
              <p className="mt-1 text-xs text-muted-foreground">{t('pbxCli.buttonConfigs.help.commandHint')}</p>
            </div>

            <div>
              <Label htmlFor="orderIndex">{t('pbxCli.buttonConfigs.fields.orderIndex')}</Label>
              <Input
                id="orderIndex"
                type="number"
                min={0}
                value={formData.orderIndex}
                onChange={(event) => handleFieldChange('orderIndex', Number(event.target.value) || 0)}
              />
              <p className="mt-1 text-xs text-muted-foreground">{t('pbxCli.buttonConfigs.help.orderHint')}</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enabled">{t('pbxCli.buttonConfigs.fields.enabled')}</Label>
                <p className="text-xs text-muted-foreground">{t('pbxCli.buttonConfigs.help.enabledHint')}</p>
              </div>
              <Toggle
                id="enabled"
                value={formData.enabled}
                onChange={(checked) => handleFieldChange('enabled', checked)}
              />
            </div>
          </div>
        </FormBox>
      </form>

      <FixedActionBar
        onSave={handleSubmit}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        hasChanges={hasChanges}
        isSaving={isSaving}
        saveText={t('common:save')}
        cancelText={t('common:cancel')}
        deleteText={t('common:delete')}
        showDelete={isEditMode}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('pbxCli.buttonConfigs.messages.deleteTitle')}
        message={t('pbxCli.buttonConfigs.messages.deleteConfirmation', { label: formData.label || '' })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

